import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchSharedScore } from '../lib/api';
import { SharedScoreResponse } from '../types/leaderboard';
import { formatLeaderboardDate } from '../lib/utils';
import '../styles/ranking.css';

const SharedScore: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [scoreData, setScoreData] = useState<SharedScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) {
      setError('Invalid score link');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadScore = async () => {
      try {
        const data = await fetchSharedScore(gameId);
        if (!cancelled) {
          setScoreData(data);
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err?.message?.includes('404')) {
            setError('Score not found. This link may be invalid or the score may have been removed.');
          } else if (err?.message?.includes('400')) {
            setError('Invalid score link format.');
          } else {
            setError('Unable to load this score at the moment. Please try again later.');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadScore();

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const getPlatformLabel = (platform: string | null): string => {
    switch (platform) {
      case 'web': return 'Web';
      case 'ios': return 'iOS';
      case 'android': return 'Android';
      default: return 'Unknown';
    }
  };

  const getGameTypeLabel = (type: string): string => {
    switch (type) {
      case 'timed': return 'Timed Challenge';
      case 'questions': return '100 Questions';
      case 'competition': return 'Grand Challenge';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="ranking-page">
        <h1 className="page-title">Shared Score</h1>
        <p className="page-subtitle">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ranking-page">
        <h1 className="page-title">Shared Score</h1>
        <p className="error-text">{error}</p>
        <Link to="/ranking" className="back-link">View Weekly Rankings</Link>
      </div>
    );
  }

  if (!scoreData) {
    return null;
  }

  return (
    <div className="ranking-page">
      <h1 className="page-title">Shared Score</h1>
      <p className="page-subtitle">
        {scoreData.player.userId ? (
          <Link to={`/profile/${encodeURIComponent(scoreData.player.displayName)}`} className="player-link">
            {scoreData.player.displayName}
          </Link>
        ) : (
          <strong>{scoreData.player.displayName}</strong>
        )}
        {' '}shared their score
      </p>

      <div className="category-card">
        <h2 className="category-card-title">{scoreData.game.categoryLabel}</h2>

        <div className="shared-score-details">
          <div className="shared-score-main">
            <div className="shared-score-value">{scoreData.score.toLocaleString()}</div>
            <div className="shared-score-label">Points</div>
          </div>

          {scoreData.rank && (
            <div className="shared-score-rank">
              <div className="shared-score-rank-value">#{scoreData.rank}</div>
              <div className="shared-score-label">Global Rank</div>
            </div>
          )}
        </div>

        <div className="table-wrapper">
          <table className="ranking-table ranking-table-no-outer-border">
            <tbody>
              <tr>
                <td style={{ textAlign: 'left', fontWeight: 'normal', width: '40%' }}>Player</td>
                <td style={{ textAlign: 'left' }}>
                  {scoreData.player.userId ? (
                    <Link to={`/profile/${encodeURIComponent(scoreData.player.displayName)}`} className="player-link">
                      {scoreData.player.displayName}
                    </Link>
                  ) : (
                    scoreData.player.displayName
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: 'left', fontWeight: 'normal' }}>Game Type</td>
                <td style={{ textAlign: 'left' }}>{getGameTypeLabel(scoreData.game.type)}</td>
              </tr>
              {scoreData.game.difficulty && (
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 'normal' }}>Difficulty</td>
                  <td style={{ textAlign: 'left' }}>{scoreData.game.difficulty}</td>
                </tr>
              )}
              {scoreData.game.length && (
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 'normal' }}>Duration</td>
                  <td style={{ textAlign: 'left' }}>{scoreData.game.length}</td>
                </tr>
              )}
              <tr>
                <td style={{ textAlign: 'left', fontWeight: 'normal' }}>Played On</td>
                <td style={{ textAlign: 'left' }}>{formatLeaderboardDate(scoreData.playedAt)}</td>
              </tr>
              {scoreData.platform && (
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 'normal' }}>Platform</td>
                  <td style={{ textAlign: 'left' }}>{getPlatformLabel(scoreData.platform)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <Link to="/ranking" className="back-link">View Weekly Rankings</Link>
        {scoreData.game.category && (
          <Link to={`/ranking/${scoreData.game.category}`} className="back-link secondary-link">
            View {scoreData.game.categoryLabel} Leaderboard
          </Link>
        )}
      </div>
    </div>
  );
};

export default SharedScore;
